import {
  AUTOMATION_LIMITS,
  AUTOMATION_PLACEHOLDERS,
  AUTOMATION_TRIGGER_DEFAULTS,
  AUTOMATION_TRIGGER_PLACEHOLDERS,
  type AutomationAction,
  type AutomationActionInput,
  type AutomationPlaceholder,
  type AutomationRuleDto,
  type AutomationTriggerInput,
  type AutomationTriggerType,
  automationRuleIssues,
} from '@fos/shared/contracts/automations';

/**
 * Stan formularza reguły (Ustawienia → Automatyzacje) i zamiana na wejście `automations.create / update`.
 * Filtry: jeden klient i jeden zasób (API przyjmuje listy — formularz wystarcza na „wszystkie” albo jeden).
 */

export interface RuleForm {
  name: string;
  trigger: AutomationTriggerType;
  clientId: string | null;
  assetId: string | null;
  daysBefore: number;
  onlyMine: boolean;
  time: string;
  weekdaysOnly: boolean;
  summary: boolean;
  brief: boolean;
  task: {
    on: boolean;
    title: string;
    assignToMe: boolean;
    dueInDays: number | null;
    priority: 'normal' | 'high' | 'urgent';
  };
  claude: { on: boolean; instruction: string; dailyLimit: number; projectId: string | null };
  notify: { on: boolean; urgent: boolean; push: boolean; email: boolean };
}

export const EMPTY_RULE_FORM: RuleForm = {
  name: '',
  trigger: 'incident.opened',
  clientId: null,
  assetId: null,
  daysBefore: 14,
  onlyMine: true,
  time: '08:00',
  weekdaysOnly: false,
  summary: false,
  brief: false,
  task: {
    on: false,
    title: AUTOMATION_TRIGGER_DEFAULTS['incident.opened'].taskTitle,
    assignToMe: true,
    dueInDays: 0,
    priority: 'high',
  },
  claude: {
    on: false,
    instruction: AUTOMATION_TRIGGER_DEFAULTS['incident.opened'].instruction,
    dailyLimit: AUTOMATION_LIMITS.claudePerDayDefault,
    projectId: null,
  },
  notify: { on: true, urgent: true, push: true, email: false },
};

export function formFromRule(rule: AutomationRuleDto): RuleForm {
  const t = rule.trigger;
  const find = <K extends AutomationAction['type']>(type: K) =>
    rule.actions.find((a) => a.type === type) as Extract<AutomationAction, { type: K }> | undefined;
  const task = find('create_task');
  const claude = find('delegate_claude');
  const notify = find('notify');
  return {
    name: rule.name,
    trigger: t.type,
    clientId: 'clientIds' in t ? (t.clientIds[0] ?? null) : null,
    assetId: 'assetIds' in t ? (t.assetIds[0] ?? null) : null,
    daysBefore: t.type === 'renewal.due' ? t.daysBefore : 14,
    onlyMine: t.type === 'task.overdue' ? t.onlyMine : true,
    time: t.type === 'schedule.daily' ? t.time : '08:00',
    weekdaysOnly: t.type === 'schedule.daily' ? t.weekdaysOnly : false,
    summary: Boolean(find('summary')),
    brief: Boolean(find('brief')),
    task: task
      ? { on: true, title: task.title, assignToMe: task.assignToMe, dueInDays: task.dueInDays, priority: task.priority }
      : EMPTY_RULE_FORM.task,
    claude: claude
      ? { on: true, instruction: claude.instruction, dailyLimit: claude.dailyLimit, projectId: claude.projectId }
      : EMPTY_RULE_FORM.claude,
    notify: notify
      ? { on: true, urgent: notify.urgent, push: notify.push, email: notify.email }
      : { ...EMPTY_RULE_FORM.notify, on: false },
  };
}

export function triggerFromForm(f: RuleForm): AutomationTriggerInput {
  const clientIds = f.clientId ? [f.clientId] : [];
  switch (f.trigger) {
    case 'incident.opened':
    case 'incident.resolved':
      return { type: f.trigger, clientIds, assetIds: f.assetId ? [f.assetId] : [] };
    case 'message.urgent':
      return { type: 'message.urgent', clientIds };
    case 'renewal.due':
      return { type: 'renewal.due', daysBefore: f.daysBefore, clientIds };
    case 'task.overdue':
      return { type: 'task.overdue', onlyMine: f.onlyMine };
    case 'schedule.daily':
      return { type: 'schedule.daily', time: f.time, weekdaysOnly: f.weekdaysOnly };
  }
}

export function actionsFromForm(f: RuleForm): AutomationActionInput[] {
  const out: AutomationActionInput[] = [];
  const schedule = f.trigger === 'schedule.daily';
  if (!schedule && f.summary) out.push({ type: 'summary' });
  if (schedule && f.brief) out.push({ type: 'brief' });
  if (f.task.on) {
    out.push({
      type: 'create_task',
      title: f.task.title,
      assignToMe: f.task.assignToMe,
      dueInDays: f.task.dueInDays,
      priority: f.task.priority,
    });
  }
  if (f.claude.on) {
    out.push({
      type: 'delegate_claude',
      instruction: f.claude.instruction,
      dailyLimit: f.claude.dailyLimit,
      projectId: f.claude.projectId,
    });
  }
  if (f.notify.on) {
    out.push({ type: 'notify', urgent: f.notify.urgent, push: f.notify.push, email: f.notify.email });
  }
  return out;
}

/** Pola szablonu dostępne dla wyzwalacza; `trustedOnly` = zlecenie dla Claude (bez treści z maila). */
export function placeholdersFor(trigger: AutomationTriggerType, trustedOnly = false): AutomationPlaceholder[] {
  return AUTOMATION_TRIGGER_PLACEHOLDERS[trigger].filter((p) => !trustedOnly || AUTOMATION_PLACEHOLDERS[p].trusted);
}

export function triggerUsesClients(t: AutomationTriggerType): boolean {
  return t === 'incident.opened' || t === 'incident.resolved' || t === 'message.urgent' || t === 'renewal.due';
}

export function triggerUsesAssets(t: AutomationTriggerType): boolean {
  return t === 'incident.opened' || t === 'incident.resolved';
}

/**
 * Zmiana wyzwalacza w edytorze (Przegląd Fali 10): tytuł zadania i instrukcja dla Claude, których osoba nie zmieniła
 * (domyślne poprzedniego wyzwalacza), przechodzą na domyślne nowego — pola szablonu zawsze pasują do wyzwalacza.
 * Tekst zmieniony przez osobę zostaje (pole spoza wyzwalacza pokaże błąd przy polu).
 */
export function withTrigger(form: RuleForm, trigger: AutomationTriggerType): RuleForm {
  const before = AUTOMATION_TRIGGER_DEFAULTS[form.trigger];
  const after = AUTOMATION_TRIGGER_DEFAULTS[trigger];
  return {
    ...form,
    trigger,
    summary: false,
    brief: trigger === 'schedule.daily',
    task: form.task.title === before.taskTitle ? { ...form.task, title: after.taskTitle } : form.task,
    claude:
      form.claude.instruction === before.instruction ? { ...form.claude, instruction: after.instruction } : form.claude,
  };
}

/** Podpowiedź akcji „Utwórz zadanie” z przykładem tytułu dla wyzwalacza (pola, które ten wyzwalacz zna). */
export function taskHintFor(trigger: AutomationTriggerType): string {
  return `Zadanie z tytułem z szablonu, np. „${AUTOMATION_TRIGGER_DEFAULTS[trigger].taskTitle}”.`;
}

export type RuleFormErrorKey = 'name' | 'trigger' | 'actions' | 'taskTitle' | 'claudeInstruction' | 'form';

/** Pole formularza dla błędu walidacji (ścieżka `actions.<i>.title` / `.instruction` = pole przy akcji). */
export function ruleErrorKey(path: string): RuleFormErrorKey {
  if (path.startsWith('actions')) {
    if (path.endsWith('.title')) return 'taskTitle';
    if (path.endsWith('.instruction')) return 'claudeInstruction';
    return 'actions';
  }
  const head = path.split('.')[0];
  return head === 'name' || head === 'trigger' ? head : 'form';
}

/** Spójność reguły przed zapisem (te same reguły co serwer): błędy przy polach. */
export function ruleFormIssues(f: RuleForm): Partial<Record<RuleFormErrorKey, string>> {
  const out: Partial<Record<RuleFormErrorKey, string>> = {};
  for (const issue of automationRuleIssues({
    trigger: triggerFromForm(f),
    actions: actionsFromForm(f) as AutomationAction[],
  })) {
    out[ruleErrorKey(issue.path.join('.'))] ??= issue.message;
  }
  return out;
}
